import { useEffect, type RefObject } from "react";

/**
 * Converted-PDF pages, which the parser renders with `content-visibility: auto`
 * (`createHtmlParserOptions`).
 */
const SKIPPABLE_PAGE_SELECTOR = ".ocr-anchor-wrapper";

/** Longest one slice may run, so a scroll or click waits at most about a frame. */
export const MAX_WARM_SLICE_MS = 16;

/** Laying out one OCR page takes ~2–10 ms; a slice starts no page with less left. */
const MIN_SLICE_REMAINING_MS = 4;

/**
 * Lays out a converted PDF's off-screen pages while the browser is idle, a few
 * pages per slice (LAR-487).
 *
 * Off-screen pages skip rendering (`content-visibility: auto`), so the first
 * page paints without laying out the other ~160. Whatever later needs them all —
 * find-in-page, the accessibility tree, a page-text capture by the browser —
 * then lays every one of them out in a single task. On dev that happened about a
 * second after each open of a 162-page OCR document: a 0.5–1 s freeze just as
 * the reader starts to scroll. Reading the geometry of an element inside a
 * skipped page lays that page out, so doing it page by page in idle time leaves
 * that task next to nothing to do.
 */
export function useWarmSkippedPages(
  docRootRef: RefObject<HTMLElement | null>,
  isContentRendered: boolean,
  contentKey: string
): void {
  useEffect(() => {
    const root = docRootRef.current;
    if (!root || !isContentRendered || !contentKey) return;

    const pages = root.querySelectorAll(SKIPPABLE_PAGE_SELECTOR);
    if (pages.length === 0) return;

    let next = 0;
    let cancel: () => void = () => {};

    const warmSlice = (timeRemaining: () => number) => {
      const sliceEnd =
        performance.now() + Math.min(timeRemaining(), MAX_WARM_SLICE_MS);
      while (
        next < pages.length &&
        sliceEnd - performance.now() > MIN_SLICE_REMAINING_MS
      ) {
        // The page's own box comes from its aspect-ratio, so measuring the page
        // lays nothing out; measuring a child lays out the whole page.
        pages[next++].firstElementChild?.getBoundingClientRect();
      }
      if (next < pages.length) cancel = whenIdle(warmSlice);
    };

    cancel = whenIdle(warmSlice);
    return () => cancel();
  }, [docRootRef, isContentRendered, contentKey]);
}

/**
 * Runs `work` once the browser is idle and returns its cancel function. Where
 * `requestIdleCallback` is missing (older Safari), a timer slice of the same
 * size stands in for it.
 */
function whenIdle(work: (timeRemaining: () => number) => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback((deadline) =>
      work(() => deadline.timeRemaining())
    );
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(() => work(() => MAX_WARM_SLICE_MS), 0);
  return () => window.clearTimeout(handle);
}
